import { ScanLine } from "lucide-react";
import { radiographyExtraCases } from "./radiographyCases";

// Radiography Positioning & Safety — Cert IV: perform veterinary radiography.
export const radiographyConfig = {
  simId: "radiography",
  simName: "VetSim Imaging",
  subtitle: "Radiography positioning & safety",
  title: "Radiography Positioning & Safety",
  accent: "#475569",
  Icon: ScanLine,
  topic: "radiography",
  steps: ["Select the view", "Position & centre", "Exposure & collimation", "Radiation safety", "QC the image"],
  introBlurb:
    "Produce a diagnostic radiograph the right way. Choose the views, drag the centring point onto the correct landmark, set kV/mAs and collimation on a live preview, apply ALARA radiation safety with a staff-dose meter, then quality-check the image. Graded against a Cert IV radiography rubric.",
  cases: [
    ...radiographyExtraCases,
    {
      id: "thorax-dog",
      name: "Rex — thoracic radiographs",
      meta: "Dog · suspected cardiac/respiratory disease",
      briefing:
        "The vet wants thoracic radiographs on Rex to assess his heart and lungs. He's panting but stable.",
      beats: [
        { type: "choice", key: "s1", label: "Select the view",
          title: "Which views?", prompt: "What's the standard thoracic series?",
          options: [
            { t: "A right lateral and a dorsoventral (or ventrodorsal) — two orthogonal views are the minimum for the thorax.", score: 3, note: "Two orthogonal views are standard; lateral + DV/VD let you localise lesions in 3D." },
            { t: "A single right lateral on its own should be plenty to assess the heart and lungs, so there's no real need to take a second view.", score: 0, note: "One view can't localise or confirm — always take orthogonal views." },
            { t: "An abdominal ventrodorsal will cover the chest fine, since the centring and the field are much the same for the thorax anyway.", score: 0, note: "Wrong region and wrong centring for the thorax." },
          ], full: 20 },
        { type: "positioning", key: "s2", label: "Position & centre",
          title: "Position & centre", prompt: "For the lateral thorax, drag the centring cross and collimation box onto the correct landmark.",
          target: { x: 38, y: 50 }, tolerance: { full: 10, half: 22 }, box: 40,
          landmark: "the caudal border of the scapula (≈ 5th–6th intercostal space)",
          coach: "Centre on the caudal scapula at the 5th–6th intercostal space, forelegs drawn cranially, and expose on peak inspiration to inflate the lungs for diagnostic detail.",
          full: 20 },
        { type: "exposure", key: "s3", label: "Exposure & collimation",
          title: "Exposure & collimation", prompt: "Dial in kV and mAs and collimate to the thorax — watch the preview.",
          kv: { min: 50, max: 120, ideal: 85, lo: 75, hi: 95 },
          mas: { min: 1, max: 16, ideal: 4, lo: 2.5, hi: 6 },
          collimation: { lo: 55, hi: 82 },
          full: 20 },
        { type: "alara", key: "s4", label: "Radiation safety",
          title: "Radiation safety (ALARA)", prompt: "Tap every genuine safety practice — watch the staff-dose meter.",
          chips: [
            { t: "Sedate/chemically restrain so no one holds the patient", flag: true, coach: "ALARA — the best dose to staff is zero; avoid manual restraint." },
            { t: "Wear lead gowns, thyroid shields and gloves if restraint is unavoidable", flag: true, coach: "PPE protects when hands-on restraint is genuinely needed." },
            { t: "Wear and monitor a personal dosimeter badge", flag: true, coach: "Dose monitoring is a legal requirement for radiation workers." },
            { t: "Keep hands out of the primary beam; maximise distance", flag: true, coach: "Distance and staying out of the primary beam dramatically cut dose." },
            { t: "Hold the patient bare-handed for a quick shot", flag: false, coach: "" },
            { t: "Let a pregnant staff member hold the plate", flag: false, coach: "" },
          ], full: 20 },
        { type: "choice", key: "s5", label: "QC the image", last: true,
          title: "Quality-check", prompt: "Your lateral looks grey and the ribs are blurred. What happened and what next?",
          options: [
            { t: "Motion blur from exposing while he panted, and possibly over-exposure — repeat with better restraint/timing on inspiration and check exposure factors, keeping ALARA in mind.", score: 3, note: "Identifies motion + exposure errors and corrects them — sound QC reasoning." },
            { t: "It looks alright enough to me, so just send it straight through to the vet and let them interpret it as it is.", score: 0, note: "A blurred, grey film is non-diagnostic — don't pass it on." },
            { t: "Just adjust the brightness and contrast on the screen until it looks a bit clearer, then call it done without repeating.", score: 0, note: "Post-processing can't fix motion blur or true exposure errors — repeat correctly." },
          ], full: 20 },
      ],
    },
  ],
};
